import type { SupabaseClient } from "@supabase/supabase-js";

/** `download` 속성용: 경로 구분자·제어문자·금지문자 제거, 빈 값 방지 */
export function sanitizeDownloadFilename(raw: string): string {
  const trimmed = raw.trim().replace(/[/\\:*?"<>|#\r\n\t]/g, "_").replace(/\u0000/g, "");
  if (!trimmed) return "download";
  return trimmed.length > 200 ? trimmed.slice(0, 200) : trimmed;
}

export function downloadBlobAs(blob: Blob, displayName: string): void {
  const safeName = sanitizeDownloadFilename(displayName);
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = safeName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Supabase Storage 객체를 원본 표시 이름으로 저장.
 * 1) Storage API download, 2) 실패 시 fallbackUrl fetch.
 */
export async function downloadSupabaseStorageFileAs(
  client: SupabaseClient,
  bucket: string,
  path: string,
  displayName: string,
  fallbackUrl?: string | null
): Promise<void> {
  const key = path.trim();
  if (!key) throw new Error("파일 경로가 없습니다.");

  let blob: Blob | null = null;
  const { data, error } = await client.storage.from(bucket).download(key);
  if (!error && data) {
    blob = data;
  } else if (fallbackUrl) {
    const res = await fetch(fallbackUrl);
    if (!res.ok) throw new Error("파일을 불러오지 못했습니다.");
    blob = await res.blob();
  }

  if (!blob) throw new Error("파일을 다운로드하지 못했습니다.");
  downloadBlobAs(blob, displayName);
}

/** 교차 출처 URL을 Blob으로 받아 원본 표시 이름으로 저장 */
export async function downloadUrlAs(url: string, displayName: string): Promise<void> {
  const res = await fetch(url);
  if (!res.ok) throw new Error("파일을 불러오지 못했습니다.");
  const blob = await res.blob();
  downloadBlobAs(blob, displayName);
}
